/*
  GEMDRAW — Official accounts rail

  WHAT THIS FILE IS ALLOWED TO DO. Two things: keep the progress bar in step
  with the rail, and bring a block into view when it is selected in the theme
  editor. That is the whole remit.

  WHAT IT DELIBERATELY DOES NOT DO. It does not move the rail, page it, or own
  the gesture. The rail is a scroll container with scroll-snap, so the browser
  already provides momentum, snapping, keyboard scrolling, trackpad, and the
  accessibility behaviour that comes with a real scroll container. A drag
  library would replace all of that with a worse copy and break text selection
  and link activation on the way — see the note on sliders in the build brief.

  So if this file never runs, the rail still scrolls, every card is still
  reachable, and the progress bar reads full rather than empty. Nothing here
  gates content.
*/

(function () {
  'use strict';

  const SECTION = '[data-gd-follow]';
  const BOUND = 'gdFollowBound';

  /*
    ONE FRAME PER SCROLL, NOT ONE HANDLER PER PIXEL.

    A scroll listener on a horizontal rail fires far faster than the screen can
    paint. The write is deferred to the next frame and coalesced, so a fast
    flick costs one style write per frame rather than forty.
  */
  function bindRail(section) {
    const rail = section.querySelector('[data-gd-follow-rail]');
    const meter = section.querySelector('[data-gd-follow-progress]');
    if (!rail) return null;

    let frame = null;

    function write() {
      frame = null;
      const travel = rail.scrollWidth - rail.clientWidth;

      /* Not scrollable — one card, or a desktop grid. A progress bar for a list
         that cannot move is noise, so it reads complete and says nothing. */
      const progress = travel > 1 ? rail.scrollLeft / travel : 1;
      section.style.setProperty('--gd-follow-progress', progress.toFixed(4));

      if (meter) {
        meter.setAttribute('aria-valuenow', String(Math.round(progress * 100)));
      }
    }

    function schedule() {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(write);
    }

    rail.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    write();

    return {
      rail: rail,
      teardown: function () {
        rail.removeEventListener('scroll', schedule);
        window.removeEventListener('resize', schedule);
        if (frame !== null) window.cancelAnimationFrame(frame);
      },
    };
  }

  function init(scope) {
    const root = scope && scope.querySelectorAll ? scope : document;
    const sections = root.matches && root.matches(SECTION) ? [root] : root.querySelectorAll(SECTION);

    Array.prototype.forEach.call(sections, function (section) {
      /* Idempotent: the editor re-renders a section in place and would
         otherwise stack a second scroll listener on every save. */
      if (section.dataset[BOUND] === 'true') return;
      section.dataset[BOUND] = 'true';

      const bound = bindRail(section);
      if (bound) section._gdFollowTeardown = bound.teardown;
    });
  }

  function destroy(section) {
    if (!section) return;
    if (typeof section._gdFollowTeardown === 'function') section._gdFollowTeardown();
    section._gdFollowTeardown = null;
    delete section.dataset[BOUND];
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  /* -- Theme editor ------------------------------------------------------------ */

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    const section = event.target.querySelector
      ? event.target.querySelector(SECTION) || event.target
      : event.target;
    destroy(section);
  });

  /*
    SELECTING A BLOCK SCROLLS TO IT, AND DOES SO INSTANTLY.

    A merchant editing the third account should not have to find it. `auto`
    rather than `smooth` on purpose: this is an editing aid, and a 400ms glide
    every time a field is focused is the kind of help that becomes an
    irritation. Only the rail scrolls — `nearest` on the block axis keeps the
    page where the merchant left it.
  */
  document.addEventListener('shopify:block:select', function (event) {
    const card = event.target;
    if (!card || !card.closest || !card.closest(SECTION)) return;

    const rail = card.closest('[data-gd-follow-rail]');
    if (!rail) return;

    card.scrollIntoView({ behavior: 'auto', inline: 'center', block: 'nearest' });
  });
})();
